(ns minorodata.data-views.api
  (:require [ajax.core :refer [json-request-format POST]]
            [ajax.protocols :as pr]
            [clojure.set :as c.set]
            [minorodata.app.api-config :refer [API_HOST]]
            [minorodata.app.posthog-events :as posthog-events]
            [minorodata.app.utils :refer [save-buffer-to-file]]
            [minorodata.app.handlers :refer [dispatch reg-event-fx]]
            [minorodata.data-views.model :refer [->data-view ->data-views-all
                                                 ->data-views-created
                                                 ->data-views-page ->data-views-shared]]
            [mold-http.core :refer [reg-event-http]]
            [re-posh.core :as re-posh]))


(defn datatable->data-view
  [entity]
  (some-> entity
          (clojure.set/rename-keys {:datatable-id :data-view-id})
          ->data-view))

(defn parse-paginated-response [{:keys [response key-rename-fn latest-order-index replace?]}]
  (let [{:keys [results pagination sort]} response
        results (mapv datatable->data-view results)
        results-count (count results)
        [start-idx end-idx] (if replace?
                              [0 results-count]
                              [latest-order-index (+ latest-order-index results-count)])
        queries (mapv (fn [idx query] (assoc query :order idx))
                      (range start-idx end-idx)
                      results)]
    (-> {:data-views queries
         :latest-order-index end-idx}
        (merge pagination sort)
        key-rename-fn)))

;;
;; Fetch all data-views
;;

(defn fetch-data-views-params [override current]
  (let [{:data-views-all/keys [page items-per-page]} current]
    {:pagination
     {:page           (or (:page override) page 1)
      :items-per-page (or (:items-per-page override) items-per-page 100)}}))


(defn fetch-data-views-request [_ [{:keys [page replace?]
                                    :as params
                                    :or {replace? false}}]]
  (let [state @(re-posh/subscribe [:data-views-all/pull])]
    (when (or (nil? page) (<= page (:data-views-all/total-pages state)))
      {:http-xhrio
       {:id        :data-views-fetch-request
        :url       (str "/api/datatables")
        :params    (fetch-data-views-params params state)
        :strategy  :keep-latest
        :cb-params replace?}})))

(defn fetch-data-views-callback [_ [error resp replace?]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to load data views"]}
    (let [{:data-views-all/keys [latest-order-index]} @(re-posh/subscribe [:data-views-all/pull])
          data-views (parse-paginated-response
                      {:response           resp
                       :replace?           replace?
                       :key-rename-fn      ->data-views-all
                       :latest-order-index latest-order-index})]
      {:dispatch [:data-views-all/update data-views {:replace replace?}]})))

(reg-event-http
 :data-views.api/fetch-all
 fetch-data-views-request
 fetch-data-views-callback)

;;
;; Fetch shared data-views
;;

(defn fetch-shared-data-views-params [override current]
  (let [{:data-views-shared/keys [page items-per-page]} current]
    {:pagination
     {:page           (or (:page override) page 1)
      :items-per-page (or (:items-per-page override) items-per-page 100)}}))

(defn fetch-shared-data-views-request [_ [{:keys [page replace?]
                                           :as params
                                           :or {replace? false}}]]
  (let [state @(re-posh/subscribe [:data-views-shared/pull])]
    (when (or (nil? page) (<= page (:data-views-shared/total-pages state)))
      {:http-xhrio
       {:id        :data-views-shared-fetch-request
        :url       (str "/api/datatables/shared")
        :params    (fetch-shared-data-views-params params state)
        :strategy  :keep-latest
        :cb-params replace?}})))

(defn fetch-shared-data-views-callback [_ [error resp replace?]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to load shared data views"]}
    (let [{:data-views-shared/keys [latest-order-index]} @(re-posh/subscribe [:data-views-shared/pull])
          data-views (parse-paginated-response
                      {:response           resp
                       :replace?           replace?
                       :key-rename-fn      ->data-views-shared
                       :latest-order-index latest-order-index})]
      {:dispatch [:data-views-shared/add-many data-views {:replace replace?}]})))

(reg-event-http
 :data-views.api/fetch-shared
 fetch-shared-data-views-request
 fetch-shared-data-views-callback)

;;
;; Fetch created data-views
;;

(defn fetch-created-data-views-params [override current]
  (let [{:data-views-shared/keys [page items-per-page]} current]
    {:pagination
     {:page           (or (:page override) page 1)
      :items-per-page (or (:items-per-page override) items-per-page 100)}}))

(defn fetch-created-data-views-request [_ [{:keys [page replace?]
                                            :as params
                                            :or {replace? false}}]]
  (let [state @(re-posh/subscribe [:data-views-created/pull])]
    (when (or (nil? page) (<= page (:data-views-created/total-pages state)))
      {:http-xhrio
       {:id        :data-views-created-fetch-request
        :url       (str "/api/datatables/my")
        :params    (fetch-created-data-views-params params state)
        :strategy  :keep-latest
        :cb-params replace?}})))

(defn fetch-created-data-views-callback [_ [error resp replace?]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to load created data views"]}
    (let [{:data-views-created/keys [latest-order-index]} @(re-posh/subscribe [:data-views-created/pull])
          data-views (parse-paginated-response
                      {:response           resp
                       :replace?           replace?
                       :key-rename-fn      ->data-views-created
                       :latest-order-index latest-order-index})]
      {:dispatch [:data-views-created/update data-views {:replace replace?}]})))

(reg-event-http
 :data-views.api/fetch-created
 fetch-created-data-views-request
 fetch-created-data-views-callback)

;;
;; Create data-view
;;

(defn create-data-view-request [_ [{:keys [params on-success]}]]
  {:http-xhrio
   {:id        :create-data-view-request
    :method    :post
    :url       (str "/api/datatables")
    :params    (update params :description #(or % ""))
    :cb-params {:on-success on-success}}})

(defn create-data-view-callback [_ [error resp {:keys [on-success]}]]
  (if (some? error)
    {:dispatch-n
     [[:app.events/add-snack :error "Failed to create the data view"]
      [:app.api/log :error "Failed to create the data view" error]]}

    (let [{:data-views-all/keys [latest-order-index]} @(re-posh/subscribe [:data-views-all/pull])
          idx (or latest-order-index 0)
          entity (:result resp)
          data-view (-> entity
                        (assoc :order idx)
                        datatable->data-view)]
      (when on-success (on-success data-view))
      {:dispatch-n [[:data-views-all/add data-view]
                    [:data-views-all/update {:data-views-all/data-views data-view
                                             :data-views-all/last-order-index (inc idx)}]
                    [:data-views-created/update {:data-views-created/data-views data-view
                                                 :data-views-created/last-order-index idx}]
                    [:app.events/add-snack :success "Data view created"]]})))

(reg-event-http
 :data-views.api/create-data-view
 create-data-view-request
 create-data-view-callback)

;;
;; Update data-view
;;

(defn update-data-view-request [_ [{:keys [id data-view]}]]
  {:http-xhrio
   {:id        :update-data-view-request
    :method    :put
    :url       (str "/api/datatables/" id)
    :params    data-view}})

(defn update-data-view-callback [_ [error resp]]
  (let [{:data-views-page/keys [sidebar-tab]} @(re-posh/subscribe [:data-views-page/pull])
        fetch-event (keyword (str "data-views.api/fetch-" sidebar-tab))]
    (if (some? error)
      {:dispatch-n
       [[:app.events/add-snack :error "Failed to update the data view"]
        [:app.api/log :error "Failed to update the data view" error]]}

      {:dispatch-n [[fetch-event {:replace? true}]
                    [:app.events/add-snack :success "Data view updated"]]})))

(reg-event-http
 :data-views.api/update-data-view
 update-data-view-request
 update-data-view-callback)

;;
;; Delete data-view
;;

(defn delete-data-view-request [_ [{:keys [id]}]]
  {:http-xhrio
   {:id        :delete-data-view-request
    :method    :delete
    :url       (str "/api/datatables/" id)
    :cb-params {:id id}}})

(defn delete-data-view-callback [_ [error _ {:keys [id]}]]
  (let [{:data-views-page/keys [sidebar-tab]} @(re-posh/subscribe [:data-views-page/pull])
        fetch-event (keyword (str "data-views.api/fetch-" sidebar-tab))]
    (if (some? error)
      {:dispatch-n
       [[:app.events/add-snack :error "Failed to delete the data view"]
        [:app.api/log :error "Failed to delete the data view" error]]}

      {:dispatch-n [[fetch-event {:replace? true}]
                    [:app.events/add-snack :success "Data view deleted"]]})))

(reg-event-http
 :data-views.api/delete-data-view
 delete-data-view-request
 delete-data-view-callback)

;;
;; Share data-view
;;

(defn share-data-view-request [_ [{:keys [id user-ids on-success]}]]
  {:http-xhrio
   {:id        :share-data-view-request
    :method    :post
    :url       (str "/api/datatables/" id "/share")
    :params    {:user-ids user-ids}
    :cb-params {:on-success on-success}}})

(defn share-data-view-callback [_ [error _ {:keys [on-success]}]]
  (if (some? error)
    {:dispatch-n
     [[:app.events/add-snack :error "Failed to share the data view"]
      [:app.api/log :error "Failed to share the data view" error]]}

    (do
      (when on-success (on-success))
      {:dispatch-n [[:app.events/add-snack :success "Data view shared"]]})))

(reg-event-http
 :data-views.api/share-data-view
 share-data-view-request
 share-data-view-callback)

;;
;; Unshare data-view
;;

(defn unshare-data-view-request [_ [{:keys [id user-id]}]]
  {:http-xhrio
   {:id        :unshare-data-view-request
    :method    :post
    :url       (str "/api/datatables/" id "/unshare")
    :params    {:user-id user-id}
    :cb-params {:id id}}})

(defn unshare-data-view-callback [_ [error _ {:keys [id]}]]
  (if (some? error)
    {:dispatch-n
     [[:app.events/add-snack :error "Failed to unshare the data view"]
      [:app.api/log :error "Failed to unshare the data view" error]]}

    {:dispatch-n [[:data-views-shared/delete [:data-view/data-view-id id]]
                  [:app.events/add-snack :success "Data view unshared"]]}))

(reg-event-http
 :data-views.api/unshare-data-view
 unshare-data-view-request
 unshare-data-view-callback)

;;
;; Preview data-view
;;

(defn preview-data-view-request [_ [{:keys [id]}]]
  {:http-xhrio
   {:id     :preview-data-view-request
    :method :post
    :url    (str "/api/datatables/" id "/preview")}})

(defn preview-data-view-callback [_ [error resp]]
  (if (some? error)
    (let [message (or (get-in error [:response :message])
                      (get-in error [:response :error]))]
      {:dispatch-n
       [[:app.events/add-snack :error (cond-> "Failed to preview query"
                                        message (str ": " message))]
        [:app.api/log :error "Failed to preview query" error]]})

    (let [data (:results resp)]
      {:dispatch [:data-views-page/update (->data-views-page {:data data})]})))


(reg-event-http
 :data-views.api/preview
 preview-data-view-request
 preview-data-view-callback)

;;
;; Download data-view
;;


(reg-event-fx
 :data-views.api/download
 (fn [_ [{:keys [data-view statement-index file-type]}]]
   (let [current-user @(re-posh/subscribe [:user/pull])
         id           (:data-view/data-view-id data-view)
         token        (:user/session-token current-user)
         url          (str API_HOST "/api/datatables/" id "/download")
         filename     (str (:data-view/name data-view) "." (case file-type
                                                             :csv "csv"
                                                             :xls "xlsx"))]

     (POST url
       {:headers         {:authorization (str "Bearer " token)}
        :params          {:statement-index statement-index
                          :file-type       file-type}
        :format          (json-request-format)
        :response-format {:content-type (if (= :csv file-type)
                                          "text/csv"
                                          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
                          :type         :arraybuffer
                          :read         pr/-body}
        :handler         (fn [array-buffer]
                           (save-buffer-to-file array-buffer filename)
                           (dispatch [:data-views-page/update (->data-views-page {:downloading? false})]))
        :error-handler   (fn [err]
                           (let [forbidden-message (when (= 403 (:status err)) "Provide valid warehouse credentials")]
                             (dispatch [:data-views-page/update (->data-views-page {:downloading? false})])
                             (dispatch [:app.events/add-snack :error (cond-> "Failed to download data view"
                                                                       forbidden-message (str ": " forbidden-message)
                                                                       :else (str ". Make sure the table is accessible and the amount of data you are trying to download is not too large."))])))
        :with-credentials true})

     {:dispatch-n     [[:data-views-page/update (->data-views-page {:downloading? true})]
                       [:app.events/add-snack :success "Downloading data view"]]
      :posthog/capture {:event (:data-view/exported posthog-events/events)
                        :props {:file-type      (name file-type)
                                :data-view-name (:data-view/name data-view)}}})))
